import React, { useState, useEffect } from "react";
import { Row, Col, Spin, Form, Card, InputNumber, Select } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";
import { BOM } from "../../../services/definedMethod";

const CASH_CURRENCY_OPTIONS = [
  { label: "NGN – Nigerian Naira", value: "NGN" },
  { label: "USD – US Dollar",      value: "USD" },
  { label: "JPY – Japanese Yen",   value: "JPY" },
];

const Tip = ({ text, tip }) => (
  <span>
    {text}
    <Tooltip title={tip}>
      <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
    </Tooltip>
  </span>
);

const AddSettlement = (props) => {
  const [form]      = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);

  const [settlementMethodOptions, setSettlementMethodOptions] = useState([]);
  const [tradeOptions,            setTradeOptions]            = useState([]);

  const navigate   = useNavigate();
  const location   = useLocation();
  const { userData } = props;
  const { session }  = userData || {};

  const settlementId = location.state || null;
  const isEdit       = !!settlementId;

  useEffect(() => {
  getTemplate(isEdit ? settlementId : null);
}, [settlementId]);

  const getTemplate = (id = null) => {
  setIsLoading(true);

  const body = { session };
  const url  = `${apiConfig.GetAuctions}/settlements/template`;

  BOM.FetchReqAction(body, url, (err, res) => {
    if (err) {
      BOM.AlertMsg(
        cnt.DANGER,
        err?.defaultUserMessage || "Failed to load settlement template.",
      );
      setIsLoading(false);
      return;
    }

    if (Array.isArray(res?.settlementMethodOptions)) {
      setSettlementMethodOptions(
        res.settlementMethodOptions.map((opt) => ({
          label: `${opt.code} – ${opt.value}`,
          value: opt.id,
        })),
      );
    }

    if (Array.isArray(res?.trades)) {
      setTradeOptions(
        res.trades.map((t) => ({
          label: t.tradeReference,
          value: t.tradeId,
        })),
      );
    }

    setIsLoading(false);

    if (id) fetchSettlementDetail(id);
  });
};

  // ── Fetch existing settlement for edit mode ───────────────────────────
  const fetchSettlementDetail = (id) => {
    setIsLoading(true);

    const body = { session };
    const url  = `${apiConfig.GetAuctions}/settlements/${id}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load settlement details.",
        );
        setIsLoading(false);
        return;
      }

      form.setFieldsValue({
        tradeId:           res.tradeId          ?? null,
        settlementMethod:  res.settlementMethod  ?? null,
        cashAmount:        res.cashAmount        ?? null,
        cashCurrencyCode:  res.cashCurrencyCode  ?? null,
        securityFaceValue: res.securityFaceValue ?? null,
      });

      setIsLoading(false);
    });
  };

  // ── Submit ────────────────────────────────────────────────────────────
  const onFinish = (values) => {
    const body = {
      method:           isEdit ? "PUT" : "POST",
      tradeId:          values.tradeId,
      settlementMethod: values.settlementMethod,
      cashAmount:       values.cashAmount,
      cashCurrencyCode: values.cashCurrencyCode,
      dateFormat:       "yyyy-MM-dd",
      locale:           "en",
      session,
      ...(values.securityFaceValue != null && {
        securityFaceValue: values.securityFaceValue,
      }),
      ...(isEdit && { settlementId }),
    };

    const url = isEdit
      ? `${apiConfig.GetAuctions}/settlements/${settlementId}`
      : `${apiConfig.GetAuctions}/settlements`;

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage ||
            err?.defaultUserMessage ||
            (isEdit ? "Failed to update settlement." : "Failed to create settlement."),
        );
        setIsLoading(false);
        return;
      }
      BOM.AlertMsg(
        cnt.SUCCESS,
        isEdit ? "Settlement updated successfully." : "Settlement created successfully.",
      );
      setIsLoading(false);
      navigate(-1);
    });
  };

  return (
    <MainLayout pageItems={staticConfig.manageSettlement}>
      <TopContent pageTitle={labelConfig.settlement} />

      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            <h3 style={{ marginBottom: 24, fontWeight: 600 }}>
              {isEdit ? "Edit Settlement" : "Create Settlement"}
            </h3>

            <Form layout="vertical" form={form} onFinish={onFinish}>

              {/* Trade & Settlement Method */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Trade"
                        tip="The trade this settlement is linked to."
                      />
                    }
                    name="tradeId"
                    rules={[{ required: true, message: "Trade is required" }]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={tradeOptions}
                      placeholder="Select Trade"
                      showSearch
                      optionFilterProp="label"
                      loading={isLoading}
                      disabled={isEdit} // trade cannot be changed once created
                    />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Settlement Method"
                        tip="The method used to settle this transaction."
                      />
                    }
                    name="settlementMethod"
                    rules={[
                      { required: true, message: "Settlement method is required" },
                    ]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={settlementMethodOptions}
                      placeholder="Select Settlement Method"
                      loading={isLoading}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Cash Amount & Cash Currency */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Cash Amount"
                        tip="The cash leg of the settlement — the actual amount to be paid or received."
                      />
                    }
                    name="cashAmount"
                    rules={[
                      { required: true, message: "Cash amount is required" },
                      {
                        type: "number",
                        min: 1,
                        message: "Cash amount must be greater than 0",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 49,250,000.00"
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>

                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Cash Currency"
                        tip="The currency in which the cash amount is denominated."
                      />
                    }
                    name="cashCurrencyCode"
                    rules={[
                      { required: true, message: "Cash currency is required" },
                    ]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={CASH_CURRENCY_OPTIONS}
                      placeholder="Select Currency"
                      disabled={isEdit} // currency cannot be changed once created
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Security Face Value — optional */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Security Face Value (Optional)"
                        tip="The nominal face value of the securities being delivered or received."
                      />
                    }
                    name="securityFaceValue"
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 50,000,000.00"
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <div
                style={{ borderTop: "1px solid #EAECF0", margin: "8px 0 20px" }}
              />

              <Row justify="end">
                <Col>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      marginRight: 12,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => form.submit()}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #BA24D5",
                      background: "#BA24D5",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    {isEdit ? "Update" : "Submit"}
                  </button>
                </Col>
              </Row>

              <button type="submit" hidden />
            </Form>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps, { rightAction })(AddSettlement);